import { parseColor } from '@/components/grid/color-utils'

type Rgba = [number, number, number, number]

const BLACK: Rgba = [0, 0, 0, 1]
const finite = (c: Rgba) => c.every((n) => Number.isFinite(n))

/**
 * Any CSS colour as [r, g, b, a]. Hex and rgb() parse directly; anything else (an hsl() theme
 * colour, a var() token such as the chart background's `hsl(var(--chart-canvas))`, a named colour)
 * is resolved by the browser against the page's current theme. A colour nothing can read is black.
 */
export function resolveColor(color: string): Rgba {
  const direct = parseColor(color)
  if (finite(direct)) return direct
  if (typeof document === 'undefined') return BLACK
  const probe = document.createElement('span')
  probe.style.color = color
  if (!probe.style.color) return BLACK
  document.body.appendChild(probe)
  const computed = getComputedStyle(probe).color
  probe.remove()
  const resolved = parseColor(computed)
  return finite(resolved) ? resolved : (paint(computed) ?? BLACK)
}

/** A colour the browser reports in a newer syntax (oklab(), color()), read back as a pixel. */
function paint(color: string): Rgba | null {
  const ctx = document.createElement('canvas').getContext('2d')
  if (!ctx) return null
  ctx.fillStyle = color
  ctx.fillRect(0, 0, 1, 1)
  const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data
  return [r, g, b, a / 255]
}
